/**
 * Token acquisition for every caller in the app: silent first, then ONE shared interactive sign-in (13.1-13, ISS-240).
 *
 * Today starts several Graph reads at once. When the cached token can't be renewed silently, each caller used to open
 * its own popup. MSAL allows one interaction at a time, so the rest threw `interaction_in_progress` and Today showed
 * "getSiteId: BrowserAuthError: interaction_in_progress" until a reload.
 *
 * Now:
 *   - callers needing interaction for the same scopes share one in-flight popup (and its token, or its rejection);
 *   - interactive work for different scopes queues on one lane, so two popups never overlap;
 *   - inside its turn, a caller retries silent first: the popup ahead may already have refreshed the cache;
 *   - `interaction_in_progress` from an interaction started elsewhere (a redirect, another tab) is waited out with a
 *     bounded backoff and a silent retry, not surfaced at once;
 *   - a popup the browser blocked (no click behind it) becomes a full-page sign-in (ISS-295).
 */

import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';

export interface TokenRequest {
  scopes: string[];
  /** Defaults to the first cached account, as `useAuth()` does (`accounts[0]`). */
  account?: AccountInfo | null;
}

export interface TokenOptions {
  /** Backoff before each silent retry while an outside interaction is in progress (about 10 s in total). */
  retryDelaysMs?: number[];
}

const DEFAULT_RETRY_DELAYS_MS = [250, 500, 1000, 1500, 2000, 2500, 2500];

/** One in-flight interactive attempt per scope set. */
const inflight = new Map<string, Promise<string>>();
/** Interactive attempts run one after another on this lane. It never rejects. */
let lane: Promise<void> = Promise.resolve();

/** Test hook: forget in-flight attempts between tests. */
export function __resetTokenState(): void {
  inflight.clear();
  lane = Promise.resolve();
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function hasCode(error: unknown, code: string): boolean {
  return error instanceof BrowserAuthError && error.errorCode === code;
}

/** Consent needed or session expired, or the silent iframe was blocked (Edge): only a popup can fix it. */
function needsInteraction(error: unknown): boolean {
  return error instanceof InteractionRequiredAuthError || hasCode(error, 'timed_out');
}

function interactionInProgress(error: unknown): boolean {
  return hasCode(error, 'interaction_in_progress');
}

/**
 * The browser refused to open the popup (ISS-295). A popup only opens straight after a click, so a token that runs out
 * while Today loads its reads always lands here. A user closing the popup is `user_cancelled`, not this.
 */
function popupBlocked(error: unknown): boolean {
  return hasCode(error, 'popup_window_error') || hasCode(error, 'empty_window_error');
}

async function silent(instance: IPublicClientApplication, scopes: string[], account: AccountInfo): Promise<string> {
  const response = await instance.acquireTokenSilent({ scopes, account });
  return response.accessToken;
}

/**
 * Wait out an interaction we didn't start, retrying silent after each delay.
 * Returns the token, or `{ error }` when silent now needs interaction or the wait ran out. Any other error is thrown.
 */
async function waitThenSilent(
  instance: IPublicClientApplication,
  scopes: string[],
  account: AccountInfo,
  delays: number[],
  initial: unknown,
): Promise<string | { error: unknown }> {
  let last = initial;
  for (const ms of delays) {
    await sleep(ms);
    try {
      return await silent(instance, scopes, account);
    } catch (error) {
      if (needsInteraction(error)) return { error };
      if (!interactionInProgress(error)) throw error;
      last = error;
    }
  }
  return { error: last };
}

function interactive(
  instance: IPublicClientApplication,
  scopes: string[],
  account: AccountInfo,
  delays: number[],
): Promise<string> {
  const key = [...scopes].sort().join(' ');
  const existing = inflight.get(key);
  if (existing) return existing;

  const attempt = lane.then(async () => {
    try {
      return await silent(instance, scopes, account);
    } catch (error) {
      if (!needsInteraction(error) && !interactionInProgress(error)) throw error;
    }
    try {
      const response = await instance.acquireTokenPopup({ scopes, account });
      return response.accessToken;
    } catch (popupError) {
      if (popupBlocked(popupError)) {
        // Sign in on the full page instead. It comes back to this same page (the bridge returns to where the sign-in
        // started), and the page loads afresh with a new token. Its promise never settles: the page is leaving.
        await instance.acquireTokenRedirect({ scopes, account });
        return new Promise<string>(() => {});
      }
      if (!interactionInProgress(popupError)) throw popupError;
      const outcome = await waitThenSilent(instance, scopes, account, delays, popupError);
      if (typeof outcome === 'string') return outcome;
      throw popupError;
    }
  });

  const settle = () => {
    if (inflight.get(key) === attempt) inflight.delete(key);
  };
  inflight.set(key, attempt);
  lane = attempt.then(settle, settle);
  return attempt;
}

/** Acquire an access token for `scopes`: silent first, then the shared interactive sign-in. */
export async function acquireToken(
  instance: IPublicClientApplication,
  request: TokenRequest,
  options: TokenOptions = {},
): Promise<string> {
  const account = request.account ?? instance.getAllAccounts()[0] ?? null;
  if (!account) throw new Error('No authenticated account.');
  const delays = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  const { scopes } = request;

  try {
    return await silent(instance, scopes, account);
  } catch (error) {
    let cause = error;
    if (interactionInProgress(cause)) {
      const outcome = await waitThenSilent(instance, scopes, account, delays, cause);
      if (typeof outcome === 'string') return outcome;
      cause = outcome.error;
    }
    if (!needsInteraction(cause)) throw cause;
    return interactive(instance, scopes, account, delays);
  }
}
